import React from 'react';

import { Block, withBlockRendering } from '../common/Block.jsx';
import { PageBottomHiddenDraggable, PageTopHiddenDraggable } from '../layout/Builder.jsx';
import { EditorContainerWrapper } from '../layout/Builder.jsx';
import Name from '../common/Name.jsx';
import { TabSelectionDropDown } from '../common/Tab.jsx';

function AccordionComponent(props) {
    const [contentMaxHeight, setContentMaxHeight] = React.useState(0);
    const [open, setOpen] = React.useState(props.builder || props.open);
    const ref = React.useRef();

    const calcContentMaxHeight = () => {
        if (!ref || !ref.current) {
            return;
        }
        let height = ref.current.scrollHeight;
        if (ref.current.children && ref.current.children.length > 0) {
            height = ref.current.children[0].scrollHeight;
        }
        if (height != contentMaxHeight) {
            setContentMaxHeight(height);
        }
    }
    React.useEffect(() => {
        if (props.builder || props.open) {
            // Give the layout engine a bit...
            setTimeout(calcContentMaxHeight, 500);
        } else if (!contentMaxHeight) {
            // Give the layout engine less than a bit...
            setTimeout(calcContentMaxHeight, 50);
        }
    }, [ref]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        setTimeout(calcContentMaxHeight, 50);
    }, [open])

    React.useEffect(() => {
        if (!window) {
            return;
        }
        window.addEventListener("resize", () => calcContentMaxHeight());
        return () => window.removeEventListener("resize", calcContentMaxHeight());
    }, []);

    const transitionDuration = '900ms';
    const headerStyle = { transition: `all 250ms linear` };
    if (props.builder) {
        headerStyle.pointerEvents = 'none';

        React.useEffect(() => {
            // Recalculate content height after design, padding or width changes
            calcContentMaxHeight();
        }, [
            props.blockConfig.block.content,
            props.blockConfig.block.paddingBottom.value,
            props.blockConfig.block.paddingLeft.value,
            props.blockConfig.block.paddingRight.value,
            props.blockConfig.block.paddingTop.value,
            props.blockConfig.layout.maxWidth.value
        ])
    }
    const arrowStyle = { transform: 'rotate(0deg)', transition: 'transform 250ms linear' };
    const contentContainerStyle = { transition: `height ${transitionDuration} cubic-bezier(0.19, 1, 0.22, 1), opacity 400ms linear` };
    const contentStyle = {};
    let contentBackgroundColor, contentConfig, headerBackgroundColorClosed, headerBackgroundColorOpen, headerContent;
    if (props.blockConfig) {
        if (props.blockConfig.block) {
            Block.mapStyleConfigProps(contentStyle, props.blockConfig.block, (propName, propValue) => {
                switch (propName) {
                    case 'backgroundColor':
                        contentBackgroundColor = propValue;
                        return true;
                    case 'content':
                        contentConfig = props.blockConfig.block.content;
                        return true;
                }
            });
        }
        if (props.blockConfig.header) {
            Block.mapStyleConfigProps(headerStyle, props.blockConfig.header, (propName, propValue, propUnit) => {
                switch (propName) {
                    case 'arrowColor':
                        arrowStyle.color = propValue;
                        return true;
                    case 'arrowSize':
                        if (propValue && propUnit) {
                            arrowStyle.fontSize = `${propValue}${propUnit}`;
                        }
                        return true;
                    case 'backgroundColorClosed':
                        headerBackgroundColorClosed = propValue;
                        return true;
                    case 'backgroundColorOpen':
                        headerBackgroundColorOpen = propValue;
                        return true;
                    case 'design':
                        headerContent = propValue;
                        return true;
                }
            });
        }
    }
    if (open) {
        arrowStyle.transform = 'rotate(180deg)';
    }
    const backgroundColor = (!open && headerBackgroundColorClosed) || contentBackgroundColor || "#fff";
    let headerBackgroundColor = headerBackgroundColorClosed;
    contentContainerStyle.height = '0px';
    contentContainerStyle.opacity = 0;
    if (open) {
        headerBackgroundColor = headerBackgroundColorOpen;
        contentContainerStyle.height = `${contentMaxHeight}px`
        contentContainerStyle.opacity = 1;
    }
    if (!headerBackgroundColor) {
        headerBackgroundColor = backgroundColor;
    }
    headerStyle.backgroundColor = headerBackgroundColor;

    const layoutStyles = Block.mapLayoutStyleConfigPropsWithPageKit({}, props);
    layoutStyles.style.backgroundColor = backgroundColor;
    layoutStyles.style.transition = `all 400ms linear`;

    // Move left/right kit padding values to the header and content containers (allows header to fully fill parent container, which can have a background color)
    if (layoutStyles.style.paddingLeft) {
        if (!headerStyle.paddingLeft) {
            headerStyle.paddingLeft = layoutStyles.style.paddingLeft;
        }
        if (!contentStyle.paddingLeft) {
            contentStyle.paddingLeft = layoutStyles.style.paddingLeft;
        }
        layoutStyles.style.paddingLeft = undefined;
    }
    if (layoutStyles.style.paddingRight) {
        if (!headerStyle.paddingRight) {
            headerStyle.paddingRight = layoutStyles.style.paddingRight;
        }
        if (!contentStyle.paddingRight) {
            contentStyle.paddingRight = layoutStyles.style.paddingRight;
        }
        layoutStyles.style.paddingRight = undefined;
    }

    let className = 'accordion';
    if (props.className) {
        className += ` ${props.className}`;
    }
    let content = (
        <div className={className} style={layoutStyles.style}>
            <div className="accordion__header" style={headerStyle} onClick={() => setOpen(!open)}>
                {
                    headerContent &&
                    <div className="richText accordion__header--content"><div dangerouslySetInnerHTML={{ __html: headerContent }} /></div>
                }
                <span className="i i-arrow-top" style={arrowStyle}></span>
            </div>
            <AccordionContent
                {...props}
                ref={ref}
                className={props.contentClassName}
                contentConfig={contentConfig}
                contentStyle={contentStyle}
                open={open}
                style={contentContainerStyle}
                onLoad={calcContentMaxHeight} />
        </div>
    )
    if (layoutStyles.containerStyle) {
        content = <div className="cont--kit__wrapper" style={layoutStyles.containerStyle}>{content}</div>
    }
    if (props.builder) {
        content = (
            <>
                <TabSelectionDropDown
                    label="STATE"
                    tab={open ? 'open' : 'closed'}
                    onBlockMove={props.onBlockMove}
                    onSelect={(value) => setOpen(value == 'open')}>
                    <option value={'closed'}>Closed</option>
                    <option value={'open'}>Open</option>
                </TabSelectionDropDown>
                {content}
            </>
        )
    }
    return content;
}

// region CONTENT

function _AccordionContentContainer(props) {
    let content;
    if (props.contentConfig && props.contentConfig.desktop && props.contentConfig.desktop.blocks) {
        content = props.onBlockRendering(props.pageType, props.contentConfig.desktop.blocks, (d) => {
            if (!d.type) {
                return;
            }
            switch (d.type.value) {
                case Name.Block.ImageV1:
                case Name.Block.ImageV2:
                    return { onLoad: props.onLoad };
            }
        });
    }
    content = (
        <React.Fragment>
            {
                props.editor &&
                <PageTopHiddenDraggable pageEmpty={!content || content.length == 0} onDragEnter={props.onDragEnter} />
            }
            {content}
            {
                props.editor &&
                <PageBottomHiddenDraggable onDragEnter={props.onDragEnter} />
            }
        </React.Fragment>
    );
    let className = 'cont--whitelabel';
    if (props.builder) {
        className += ' builder';
    }
    const contentProps = {
        className,
        id: props.id,
        style: props.contentStyle,
        onDragOver: (e) => { if (e) { e.preventDefault() } },
        onDrop: props.onDrop
    }
    if (props.editor) {
        contentProps.className += ' builder-editor'
        return <EditorContainerWrapper {...contentProps}>{content}</EditorContainerWrapper>
    }
    return <div {...contentProps}>{content}</div>
}
const AccordionContentContainer = withBlockRendering(_AccordionContentContainer);
const AccordionContent = React.forwardRef(function (props, ref) {
    let className = 'accordion__content';
    if (props.className) {
        className += ` ${props.className}`;
    }
    return (
        <div className={className} style={props.style} ref={ref}>
            <AccordionContentContainer {...props} />
        </div>
    );
});

export default { Component: AccordionComponent, ContentContainer: AccordionContentContainer };